import { useRef, useState } from "react";
import { Camera, FileUp, Loader2, ScanLine } from "lucide-react";
import { useAuth } from "../contexts/auth-context";
import { createContractFromUpload } from "../lib/platform-contracts";
import type { OpenedFile } from "../lib/file";
import { detectKind } from "../lib/file";

type Props = {
  apiBase: string;
  onBack: () => void;
  onOpenContract: (contractId: string, title: string) => void;
  onOpenLocalFile: (file: OpenedFile) => void;
  onStartScan: () => void;
};

export function DocumentUploadPage({
  apiBase,
  onBack,
  onOpenContract,
  onOpenLocalFile,
  onStartScan,
}: Props) {
  const { isAuthenticated, openLogin } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const pendingFile = useRef<File | null>(null);

  function isAllowed(file: File): boolean {
    const name = file.name || "";
    const mime = file.type || "";
    return (
      /\.(docx?|pdf)$/i.test(name) ||
      mime === "application/pdf" ||
      mime === "application/msword" ||
      mime ===
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    );
  }

  async function processFile(file: File) {
    if (!isAllowed(file)) {
      setError("فقط فایل‌های Word (DOC/DOCX) یا PDF مجاز هستند.");
      return;
    }
    setFileName(file.name);
    setError(null);

    if (!isAuthenticated) {
      pendingFile.current = file;
      // Open locally for guest; cloud upload after login.
      const buffer = await file.arrayBuffer();
      const kind = detectKind(file.name, file.type);
      if (kind === "pdf" || kind === "docx") {
        onOpenLocalFile({
          name: file.name,
          kind,
          buffer,
          mimeType: file.type || undefined,
        });
        return;
      }
      openLogin(() => void uploadToPlatform(file));
      return;
    }
    await uploadToPlatform(file);
  }

  async function uploadToPlatform(file: File) {
    setBusy(true);
    setError(null);
    try {
      const contract = await createContractFromUpload(apiBase, file);
      onOpenContract(contract.id, contract.title || file.name);
    } catch (err) {
      // Fallback: open locally
      try {
        const buffer = await file.arrayBuffer();
        const kind = detectKind(file.name, file.type);
        if (kind === "pdf" || kind === "docx") {
          onOpenLocalFile({
            name: file.name,
            kind,
            buffer,
            mimeType: file.type || undefined,
          });
          return;
        }
      } catch {
        /* ignore */
      }
      setError(err instanceof Error ? err.message : "بارگذاری ناموفق");
    } finally {
      setBusy(false);
      pendingFile.current = null;
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  return (
    <div className="flow-page consumer-shell">
      <header className="flow-page-head">
        <button type="button" className="text-back" onClick={onBack}>
          ← بازگشت
        </button>
        <h1>بارگذاری سند</h1>
        <div className="dashboard-feature-chips" aria-label="فرمت‌ها">
          <span className="dashboard-feature-chip">PDF</span>
          <span className="dashboard-feature-chip">Word</span>
          <span className="dashboard-feature-chip">اسکن</span>
        </div>
        <div className="hero-art">
          <img src="/illustrations/scan-hero.svg" alt="" />
        </div>
      </header>

      <div className="upload-drop-card">
        <FileUp size={36} aria-hidden />
        <p>انتخاب فایل</p>
        <input
          ref={inputRef}
          type="file"
          hidden
          accept=".doc,.docx,.pdf,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void processFile(f);
          }}
        />
        <button
          type="button"
          className="primary-btn"
          disabled={busy}
          onClick={() => inputRef.current?.click()}
        >
          {busy ? <Loader2 size={16} className="spin" /> : <FileUp size={16} />}
          {busy ? "در حال پردازش…" : "انتخاب فایل"}
        </button>
        {fileName && busy ? (
          <p className="settings-hint">در حال پردازش «{fileName}»…</p>
        ) : null}
      </div>

      <button type="button" className="flow-option-card" onClick={onStartScan}>
        <ScanLine size={22} className="flow-option-icon" />
        <div className="flow-option-copy">
          <strong>اسکن دوربین</strong>
          <span>برش و PDF</span>
        </div>
        <Camera size={18} />
      </button>

      {error ? <p className="reader-login-error">{error}</p> : null}
    </div>
  );
}
